import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  ButtonGroup,
  Card,
  Col,
  Dropdown,
  Form,
  Nav,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import {
  AlertTriangle,
  Clock,
  Download,
  Play,
  RefreshCw,
  RotateCcw,
  Zap,
} from "react-feather";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Link } from "react-router";
import { useGet, useGetLazy } from "../../api";
import { useApi } from "../../hooks";
import { ReportStatCard } from "../../components/stat-card";
import {
  AUTO_COUPON_COST_REPORT_MAX_DAYS,
  IAutoCouponSessionStats,
  IAutoCouponSessionMerchantsCostReportResponse,
  IAutoCouponSessionMerchantSpendEntry,
} from "../../interfaces/auto-coupons";
import {
  failureReasonBadge,
  failureReasonLabel,
} from "../../interfaces/failure-reasons";
import {
  IMerchantSimple,
  TTemplateCreationStatus,
} from "../../interfaces/merchant";
import {
  ITemplateCaptureRun,
  TTemplateCaptureRunStatus,
} from "../../interfaces/template-capture-run";

const PAGE_SIZE = 20;

/** Badge styling + label for a template's creation lifecycle, consistent with the Run mode badge pattern in this file. */
const TEMPLATE_STATUS_META: Record<
  TTemplateCreationStatus,
  { label: string; bg: string; text?: "dark" | "light" }
> = {
  not_created: { label: "Not created", bg: "light", text: "dark" },
  created: { label: "Created", bg: "success" },
  failed: { label: "Failed", bg: "danger" },
};

const templateStatusBadge = (
  status: TTemplateCreationStatus | undefined
): React.ReactNode => {
  const meta = TEMPLATE_STATUS_META[status ?? "not_created"];
  return (
    <Badge bg={meta.bg} text={meta.text as any}>
      {meta.label}
    </Badge>
  );
};

/** Whether both the Auto and SKU templates are already marked "created" for
 *  this merchant. Used only to swap the "Create templates" button's tooltip
 *  to a "Rebuild templates" message — the button itself stays clickable so a
 *  merchant's templates can be rebuilt (see usage below). */
const bothTemplatesCreated = (merchant: IMerchantSimple): boolean =>
  merchant.autoTemplateCreationStatus === "created" &&
  merchant.skuTemplateCreationStatus === "created";

/** Formats cumulative AI spend as a dollar amount, or "—" when absent/zero. */
const formatAiSpend = (merchant: IMerchantSimple): string => {
  const totalCost = merchant.aiSpend?.totalCost;
  if (!totalCost) return "—";
  return `$${totalCost.toFixed(2)}`;
};

/** Scopes for the bulk "Run all new" split button. */
type RunScope = "never" | "not_today" | "failed_today";

const RUN_SCOPE_LABELS: Record<RunScope, string> = {
  never: "Run all new",
  not_today: "Run those that didn't run today",
  failed_today: "Re-run all that failed today",
};

// ─── Headless template capture ("Create templates") ───────────────────────

/**
 * The `/auto-coupons/capture-runs/latest` response shape: one entry per
 * requested merchantId, `null` when the merchant has never had a capture
 * run. Carries just enough to render a status badge for the merchant's
 * latest capture run (`captureRunId` is kept for reference/debugging only —
 * review of the AI-built template happens outside this page, via the
 * checkout-extension flow, followed by manual promotion to SQL).
 */
interface ICaptureRunLatest {
  captureRunId: string;
  status: TTemplateCaptureRunStatus;
  createdAt: string;
}

/** Run states worth polling for and worth disabling "Create templates" for. */
const CAPTURE_RUN_IN_FLIGHT_STATUSES = new Set<TTemplateCaptureRunStatus>([
  "queued",
  "claimed",
  "running",
]);

// ─── Stats cards ─────────────────────────────────────────────────────────────

const StatCard: React.FC<{
  title: string;
  subtitle: string;
  value: number | null;
  secondary?: string | null;
  detail?: React.ReactNode;
  loading: boolean;
}> = ({ title, subtitle, value, secondary, detail, loading }) => (
  <Card className="h-100">
    <Card.Body className="py-2 px-3">
      <div className="small text-muted">{title}</div>
      <div className="d-flex align-items-baseline gap-2">
        <span className="fs-4 fw-semibold">
          {loading || value === null ? "…" : value}
        </span>
        {secondary && !loading && (
          <span className="small text-muted">{secondary}</span>
        )}
      </div>
      <div className="small text-muted">{subtitle}</div>
      {detail && !loading && <div className="small mt-1">{detail}</div>}
    </Card.Body>
  </Card>
);

const SessionStatsCards: React.FC = () => {
  const { data, loading, error, execute } =
    useGetLazy<IAutoCouponSessionStats>();

  React.useEffect(() => {
    execute("/auto-coupons/session-stats").catch(() => {
      // handled via the hook's error state
    });
  }, [execute]);

  const handleRefresh = () => {
    execute("/auto-coupons/session-stats?refresh=true").catch(() => {
      // handled via the hook's error state
    });
  };

  return (
    <div className="mb-3">
      <Row className="g-2">
        <Col md={3} sm={6}>
          <StatCard
            title="Session merchants"
            subtitle="With a captured session"
            value={data?.sessionMerchants.total ?? null}
            secondary={
              data
                ? `${data.sessionMerchants.inSessionMode} in session mode`
                : null
            }
            detail={
              data
                ? `${data.sessionMerchants.capturedLast24h} captured last 24h`
                : null
            }
            loading={loading}
          />
        </Col>
        <Col md={3} sm={6}>
          <StatCard
            title="Templates created (24h)"
            subtitle="New staging templates"
            value={data?.templatesCreated.last24h ?? null}
            secondary={data ? `${data.templatesCreated.valid} valid` : null}
            loading={loading}
          />
        </Col>
        <Col md={3} sm={6}>
          <StatCard
            title="Tested by QA (24h)"
            subtitle="Extension evaluations"
            value={data?.testedByUsers.evaluations ?? null}
            secondary={data ? `${data.testedByUsers.passed} passed` : null}
            detail={
              data ? `${data.testedByUsers.domains} domains covered` : null
            }
            loading={loading}
          />
        </Col>
        <Col md={3} sm={6}>
          <StatCard
            title="Agentic runs (24h)"
            subtitle="Session replays by the runner"
            value={data?.agenticRuns.total ?? null}
            secondary={
              data
                ? `${data.agenticRuns.working} working · ${data.agenticRuns.failed} failed`
                : null
            }
            loading={loading}
          />
        </Col>
      </Row>
      <div className="d-flex align-items-center gap-2 mt-1">
        <Button
          variant="link"
          size="sm"
          className="p-0 small text-decoration-none"
          onClick={handleRefresh}
          disabled={loading}
        >
          <RefreshCw size={12} className="me-1" />
          Refresh stats
        </Button>
        {data?.cachedAt && (
          <span className="small text-muted">
            as of {new Date(data.cachedAt).toLocaleTimeString()}
          </span>
        )}
        {error && (
          <span className="small text-danger">Failed to load stats.</span>
        )}
      </div>
      {data && data.agenticRuns.failureReasons.length > 0 && (
        <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
          <span className="small text-muted">Failure reasons (24h):</span>
          {data.agenticRuns.failureReasons.map((fr) => (
            <Badge
              key={fr.reason}
              bg={failureReasonBadge(fr.reason).bg}
              text={failureReasonBadge(fr.reason).text as any}
              title={fr.reason}
            >
              {failureReasonLabel(fr.reason)}: {fr.count}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Reporting tab (AI cost analytics) ─────────────────────────────────────

const MERCHANT_COST_DAY_RANGE_OPTIONS = [
  1,
  7,
  14,
  AUTO_COUPON_COST_REPORT_MAX_DAYS,
];

/** "24h" reads clearer than "1d" for the shortest window — everything else
 *  is just "{n}d". Note this is the UTC calendar day so far (same bucketing
 *  as every other window), not a true rolling 24-hour window — so right
 *  after UTC midnight it can look sparse. */
const dayRangeLabel = (d: number): string => (d === 1 ? "24h" : `${d}d`);

/** $0.0031-style precision for tiny averages, $142.50-style otherwise —
 *  plain .toFixed(2) alone would render a genuinely-nonzero sub-cent figure
 *  as a misleading "$0.00". */
const formatUsd = (value: number): string =>
  value > 0 && value < 0.01 ? `$${value.toFixed(4)}` : `$${value.toFixed(2)}`;

/** One row in the "Needs attention" callout / leaderboard flag column — the
 *  only flag this report currently computes. Keep the dollar figure in sync
 *  with AI_COST_FLAG_THRESHOLDS.MIN_SPEND_USD
 *  (packages/backend/src/constants/ai-cost-flags.ts) if it changes. */
const HIGH_SPEND_NO_TEMPLATE_HINT =
  "$3+ lifetime AI spend, but neither Auto nor SKU template has been built successfully yet";
/** This report only has one flag, but it's a dollar-driven one — use the
 *  same "danger" (red) treatment the Runs page uses for its spend-driven
 *  flags, so the color language for "this is about money" is consistent
 *  across both reporting tabs. */
const HIGH_SPEND_BADGE_BG = "danger";

const SessionMerchantsReportingTab: React.FC = () => {
  const [days, setDays] = useState(1);
  const [downloading, setDownloading] = useState<
    "daily" | "leaderboard" | null
  >(null);
  const [refreshing, setRefreshing] = useState(false);

  // useGetLazy (manual execute) rather than useGet's auto-refetch, so a
  // "force refresh" can hit `&refresh=true` for one call without that flag
  // getting stuck on the URL for every later day-range switch — mirrors
  // SessionStatsCards' own refresh pattern further up this file.
  const { data, loading, error, execute } =
    useGetLazy<IAutoCouponSessionMerchantsCostReportResponse>();
  const { execute: fetchCsv } = useGetLazy<string>();

  useEffect(() => {
    execute(`/auto-coupons/session-merchants/cost-report?days=${days}`).catch(
      () => {
        // Surfaced via the `error` state below.
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const entries = data?.daily ?? [];
  const flaggedMerchants = data?.flaggedMerchants ?? [];

  const avgLifetimePerMerchant =
    data && data.sessionMerchantsWithSpend > 0
      ? data.lifetimeTotalCost / data.sessionMerchantsWithSpend
      : 0;
  const withSpendPct =
    data && data.sessionMerchantCount > 0
      ? Math.round(
          (data.sessionMerchantsWithSpend / data.sessionMerchantCount) * 100
        )
      : 0;

  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      await execute(
        `/auto-coupons/session-merchants/cost-report?days=${days}&refresh=true`
      );
    } catch {
      // Surfaced via the `error` state below.
    } finally {
      setRefreshing(false);
    }
  };

  const download = async (mode: "daily" | "leaderboard") => {
    setDownloading(mode);
    try {
      const csv = await fetchCsv(
        `/auto-coupons/session-merchants/cost-report/export?days=${days}&mode=${mode}`,
        { responseType: "text" }
      );
      if (!csv) return;
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = data?.latestDate ?? new Date().toISOString().slice(0, 10);
      a.download = `session-merchants-cost-report-${mode}-${stamp}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div>
      {/* Controls */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div className="d-flex align-items-center gap-2">
          <span className="small text-muted">Window:</span>
          <ButtonGroup size="sm">
            {MERCHANT_COST_DAY_RANGE_OPTIONS.map((d) => (
              <Button
                key={d}
                variant={d === days ? "primary" : "outline-secondary"}
                onClick={() => setDays(d)}
              >
                {dayRangeLabel(d)}
              </Button>
            ))}
          </ButtonGroup>
        </div>
        <div className="d-flex align-items-center gap-2">
          <Button
            size="sm"
            variant="outline-primary"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            {refreshing ? (
              <Spinner animation="border" size="sm" className="me-1" />
            ) : (
              <RefreshCw size={14} className="me-1" />
            )}
            {refreshing ? "Refreshing…" : "Refresh"}
          </Button>
          <Dropdown as={ButtonGroup} size="sm">
            <Button
              variant="outline-success"
              onClick={() => download("daily")}
              disabled={!!downloading || entries.length === 0}
              title="Export one row per day (window trend)"
            >
              {downloading === "daily" ? (
                <Spinner animation="border" size="sm" className="me-1" />
              ) : (
                <Download size={14} className="me-1" />
              )}
              CSV: daily trend
            </Button>
            <Dropdown.Toggle
              split
              variant="outline-success"
              disabled={!!downloading}
            />
            <Dropdown.Menu align="end">
              <Dropdown.Item
                onClick={() => download("leaderboard")}
                disabled={!!downloading}
                title="Export every session merchant's lifetime AI spend (uncapped)"
              >
                {downloading === "leaderboard" ? (
                  <Spinner animation="border" size="sm" className="me-2" />
                ) : (
                  <Download size={13} className="me-2" />
                )}
                CSV: full leaderboard
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      {loading && !data && (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      )}
      {error && <Alert variant="danger">Failed to load report.</Alert>}

      {!error && data && (
        <>
          {/* ── Lifetime KPIs — NOT affected by the day-range control ── */}
          <div className="small text-muted mb-1">Lifetime</div>
          <Row className="g-2 mb-3">
            <ReportStatCard
              label="Lifetime AI spend"
              value={formatUsd(data.lifetimeTotalCost)}
              color="primary"
            />
            <ReportStatCard
              label="Avg spend / merchant"
              value={formatUsd(avgLifetimePerMerchant)}
              color="dark"
            />
            <ReportStatCard
              label="Merchants with spend"
              value={data.sessionMerchantsWithSpend}
              color="dark"
              sub={`${withSpendPct}% of ${data.sessionMerchantCount}`}
            />
            <ReportStatCard
              label="Merchants flagged"
              value={flaggedMerchants.length}
              color={flaggedMerchants.length > 0 ? "warning" : "success"}
            />
          </Row>

          {/* ── Needs attention ── */}
          {flaggedMerchants.length > 0 && (
            <Card className="mb-3 border-warning">
              <Card.Header className="py-2 bg-warning bg-opacity-10">
                <span className="fw-semibold small">
                  Needs attention ({flaggedMerchants.length} merchant
                  {flaggedMerchants.length === 1 ? "" : "s"})
                </span>
              </Card.Header>
              <Card.Body className="p-0">
                {/* Capped to ~5 visible rows, scrollable beyond that — this
                    list has no server-side limit (grows with however many
                    merchants actually match the flag), so it needs to stay
                    usable as it grows rather than pushing the page down
                    indefinitely. */}
                <div style={{ maxHeight: 195, overflowY: "auto" }}>
                  <Table size="sm" hover className="mb-0 small">
                    <tbody>
                      {flaggedMerchants.map((m) => (
                        <tr key={m.merchantId}>
                          <td className="fw-semibold">
                            <Link to={`/merchants/${m.merchantId}`}>
                              {m.domains[0] ?? m.name}
                            </Link>
                          </td>
                          <td>
                            <Badge
                              bg={HIGH_SPEND_BADGE_BG}
                              title={HIGH_SPEND_NO_TEMPLATE_HINT}
                            >
                              High spend, no template
                            </Badge>
                          </td>
                          <td className="text-muted">
                            {formatUsd(m.totalCost)} lifetime · auto:{" "}
                            {m.autoTemplateCreationStatus} · sku:{" "}
                            {m.skuTemplateCreationStatus}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          )}

          {/* ── Window trend — day-range control affects this section only ── */}
          <div className="small text-muted mb-1 text-uppercase">
            Last {dayRangeLabel(days)}
          </div>
          <Row className="g-2 mb-3">
            <ReportStatCard
              label="AI cost in window"
              value={formatUsd(data.totals.totalCost)}
              color="primary"
              sub={dayRangeLabel(days)}
            />
          </Row>

          <Card className="mb-3">
            <Card.Header className="py-2">
              <span className="fw-semibold small">
                Daily AI cost ({dayRangeLabel(days)})
              </span>
            </Card.Header>
            <Card.Body className="p-2">
              <ResponsiveContainer width="100%" height={320}>
                <LineChart
                  data={entries}
                  margin={{ top: 4, right: 16, bottom: 0, left: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v) => String(v).slice(5)}
                  />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v) => `$${v}`}
                  />
                  <Tooltip
                    contentStyle={{ fontSize: 12 }}
                    labelFormatter={(l) => `Date: ${l}`}
                    formatter={(v: number | undefined) => [
                      v != null ? formatUsd(v) : "—",
                      "Cost",
                    ]}
                  />
                  <Line
                    type="monotone"
                    dataKey="totalCost"
                    name="Cost"
                    stroke="#0d6efd"
                    strokeWidth={2}
                    dot={entries.length <= 31}
                    activeDot={{ r: 4 }}
                    isAnimationActive={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>

          {/* ── Leaderboard — lifetime, NOT affected by the day-range control ── */}
          <Card>
            <Card.Header className="py-2">
              <span className="fw-semibold small">
                Top session merchants by lifetime AI spend
              </span>
            </Card.Header>
            <Card.Body className="p-0">
              <Table size="sm" hover className="mb-0 small">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Domain</th>
                    <th>Country</th>
                    <th>Lifetime AI spend</th>
                    <th>Flag</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.leaderboard ?? []).map(
                    (m: IAutoCouponSessionMerchantSpendEntry, i: number) => (
                      <tr
                        key={m.merchantId}
                        className={
                          m.flag
                            ? `bg-${HIGH_SPEND_BADGE_BG} bg-opacity-10`
                            : undefined
                        }
                      >
                        <td className="text-muted">{i + 1}</td>
                        <td>
                          <Link to={`/merchants/${m.merchantId}`}>
                            {m.domains[0] ?? m.name}
                          </Link>
                          {m.domains.length > 1 && (
                            <span
                              className="text-muted small ms-1"
                              title={m.domains.slice(1).join(", ")}
                            >
                              +{m.domains.length - 1}
                            </span>
                          )}
                        </td>
                        <td>{m.countryCode ?? "—"}</td>
                        <td>{formatUsd(m.totalCost)}</td>
                        <td>
                          {m.flag && (
                            <Badge
                              bg={HIGH_SPEND_BADGE_BG}
                              title={HIGH_SPEND_NO_TEMPLATE_HINT}
                            >
                              High spend, no template
                            </Badge>
                          )}
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </>
      )}
    </div>
  );
};

/** Light polling cadence for `/auto-coupons/capture-runs/latest`, matching
 *  session-runs.tsx's own POLL_MS. */
const CAPTURE_POLL_MS = 10_000;

/**
 * Minimal view for template-side QA users: merchants with a captured
 * session, with a one-click trigger for an agentic session run. The run
 * replays the captured session and tests the templates + coupon codes
 * exactly like the extension does.
 */
export const AutoCouponSessionMerchants: React.FC = () => {
  const [view, setView] = useState<"live" | "reporting">("live");
  const [page, setPage] = useState(1);
  const [domainInput, setDomainInput] = useState("");
  const [countryInput, setCountryInput] = useState("");
  const [appliedFilters, setAppliedFilters] = useState({
    domain: "",
    countryCode: "",
  });
  const [ranStatus, setRanStatus] = useState<"" | "never" | "ran">("");
  const [triggeringId, setTriggeringId] = useState<string | null>(null);
  const [runningScope, setRunningScope] = useState<RunScope | null>(null);
  const [runProgress, setRunProgress] = useState<{
    done: number;
    total: number;
  } | null>(null);
  // ── Headless template capture ("Create templates") ────────────────────────
  const [creatingId, setCreatingId] = useState<string | null>(null);
  const [captureStatuses, setCaptureStatuses] = useState<
    Record<string, ICaptureRunLatest | null>
  >({});
  const { get, post } = useApi();
  const { data: captureEnabledData, execute: fetchCaptureEnabled } =
    useGetLazy<{ enabled: boolean }>();
  const { data: latestCaptureData, execute: fetchLatestCaptureRuns } =
    useGetLazy<Record<string, ICaptureRunLatest | null>>();
  // Default true so the button isn't shown greyed-out for the instant before
  // the enabled flag resolves.
  const captureEnabled = captureEnabledData?.enabled ?? true;

  const queryParams = new URLSearchParams({
    page: String(page),
    limit: String(PAGE_SIZE),
    hasSession: "true",
  });
  if (appliedFilters.domain) queryParams.set("domain", appliedFilters.domain);
  if (appliedFilters.countryCode)
    queryParams.set("countryCode", appliedFilters.countryCode);
  if (ranStatus) queryParams.set("ranStatus", ranStatus);

  const { data, loading, error, refetch } = useGet<IMerchantSimple[]>(
    `/merchants?${queryParams}`
  );

  const merchants = data ?? [];

  // Fetch the kill-switch flag once on mount.
  useEffect(() => {
    fetchCaptureEnabled("/auto-coupons/capture-runs/enabled").catch(() => {
      // handled via the hook's error state; captureEnabled stays optimistic
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Stable ref so the polling interval always sees the current visible page
  // of merchant ids without needing to be in its own dependency array
  // (mirrors session-runs.tsx's runsUrlRef pattern).
  const merchantIdsKey = merchants.map((m) => m._id).join(",");
  const merchantIdsKeyRef = useRef(merchantIdsKey);
  merchantIdsKeyRef.current = merchantIdsKey;

  const refetchCaptureStatuses = React.useCallback(() => {
    if (!merchantIdsKeyRef.current) return;
    fetchLatestCaptureRuns(
      `/auto-coupons/capture-runs/latest?merchantIds=${merchantIdsKeyRef.current}`
    ).catch(() => {
      // handled via the hook's error state
    });
  }, [fetchLatestCaptureRuns]);

  // Re-fetch capture-run statuses whenever the visible page of merchants changes.
  useEffect(() => {
    refetchCaptureStatuses();
  }, [merchantIdsKey, refetchCaptureStatuses]);

  useEffect(() => {
    if (latestCaptureData) setCaptureStatuses(latestCaptureData);
  }, [latestCaptureData]);

  // Light polling — scoped to only run while at least one visible row has an
  // in-flight capture run, so this doesn't poll forever once every row is
  // settled (there's nothing further to wait for once a run is terminal).
  const hasActiveCaptureRuns = Object.values(captureStatuses).some(
    (v) => v && CAPTURE_RUN_IN_FLIGHT_STATUSES.has(v.status)
  );

  // Only while the Live tab is showing — no point polling behind Reporting.
  useEffect(() => {
    if (!hasActiveCaptureRuns || view !== "live") return;
    const id = setInterval(refetchCaptureStatuses, CAPTURE_POLL_MS);
    return () => clearInterval(id);
  }, [hasActiveCaptureRuns, view, refetchCaptureStatuses]);

  const applyFilters = () => {
    setAppliedFilters({ domain: domainInput, countryCode: countryInput });
    setPage(1);
  };

  const clearFilters = () => {
    setDomainInput("");
    setCountryInput("");
    setRanStatus("");
    setAppliedFilters({ domain: "", countryCode: "" });
    setPage(1);
  };

  const onFilterKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") applyFilters();
  };

  const triggerSessionRun = async (merchant: IMerchantSimple) => {
    try {
      setTriggeringId(merchant._id);
      const res = await get<{ message: string }>(
        `/test/session-run?merchantId=${merchant._id}`
      );
      window.alert(res?.message ?? "Session run triggered");
      if (refetch) await refetch();
    } catch (err: any) {
      window.alert(
        err?.response?.data?.message ?? "Failed to trigger session run"
      );
    } finally {
      setTriggeringId(null);
    }
  };

  /** Queues an unattended AI build (runType "capture") from this merchant's
   *  captured session. Optimistically records the freshly-queued status so
   *  the row's badge/polling picks it up immediately, without waiting for
   *  the next `/capture-runs/latest` tick. */
  const createTemplates = async (merchant: IMerchantSimple) => {
    try {
      setCreatingId(merchant._id);
      const res = await post<{
        message: string;
        captureRunId: string;
        correlationId: string;
        status: TTemplateCaptureRunStatus;
      }>("/auto-coupons/capture-run", { merchantId: merchant._id });
      window.alert(res?.message ?? "Capture run queued.");
      if (res) {
        setCaptureStatuses((prev) => ({
          ...prev,
          [merchant._id]: {
            captureRunId: res.captureRunId,
            status: res.status,
            createdAt: new Date().toISOString(),
          },
        }));
      }
    } catch (err: any) {
      window.alert(
        err?.response?.data?.message ?? "Failed to queue capture run"
      );
    } finally {
      setCreatingId(null);
    }
  };

  /**
   * Bulk trigger: ask the backend which merchants match the scope, confirm the
   * count, then fan out one session run each (reusing the per-merchant
   * endpoint) while showing progress.
   */
  const runScope = async (scope: RunScope) => {
    const label = RUN_SCOPE_LABELS[scope];
    try {
      const candidates = await get<{ merchantIds: string[]; count: number }>(
        `/test/session-run-candidates?scope=${scope}`
      );
      const ids = candidates?.merchantIds ?? [];
      if (ids.length === 0) {
        window.alert(`Nothing to run — no merchants matched "${label}".`);
        return;
      }
      if (
        !window.confirm(
          `${label}: trigger a session run for ${ids.length} merchant${
            ids.length === 1 ? "" : "s"
          }? This may take a while.`
        )
      ) {
        return;
      }

      setRunningScope(scope);
      let succeeded = 0;
      let failed = 0;
      for (let i = 0; i < ids.length; i++) {
        setRunProgress({ done: i, total: ids.length });
        try {
          await get(`/test/session-run?merchantId=${ids[i]}`);
          succeeded += 1;
        } catch {
          failed += 1;
        }
      }
      setRunProgress({ done: ids.length, total: ids.length });
      window.alert(
        `Triggered ${succeeded} session run${succeeded === 1 ? "" : "s"}` +
          (failed ? `, ${failed} failed to start.` : ".")
      );
      if (refetch) await refetch();
    } catch (err: any) {
      window.alert(
        err?.response?.data?.message ??
          err?.message ??
          "Failed to start session runs"
      );
    } finally {
      setRunningScope(null);
      setRunProgress(null);
    }
  };

  const lastRan = (merchant: IMerchantSimple): string => {
    const last = merchant.schedule?.lastRun ?? merchant.lastCouponCheck;
    return last ? new Date(last).toLocaleString() : "Never";
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Agentic Session Merchants</h4>
        {view === "live" && (
          <div className="d-flex align-items-center gap-2">
            <Dropdown as={ButtonGroup} align="end">
              <Button
                variant="dark"
                size="sm"
                onClick={() => runScope("never")}
                disabled={runningScope !== null}
                title="Trigger a session run for every merchant that has never run"
              >
                {runningScope !== null ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-1" />
                    {runProgress
                      ? `${runProgress.done}/${runProgress.total}`
                      : "Running…"}
                  </>
                ) : (
                  <>
                    <Play size={14} className="me-1" />
                    Run all new
                  </>
                )}
              </Button>
              <Dropdown.Toggle
                split
                variant="dark"
                size="sm"
                id="run-all-new-dropdown"
                disabled={runningScope !== null}
              />
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => runScope("not_today")}>
                  <Clock size={14} className="me-2" />
                  Run those that didn&apos;t run today
                </Dropdown.Item>
                <Dropdown.Item onClick={() => runScope("failed_today")}>
                  <RotateCcw size={14} className="me-2" />
                  Re-run all that failed today
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => refetch()}
              disabled={runningScope !== null}
            >
              <RefreshCw size={14} className="me-1" /> Refresh
            </Button>
          </div>
        )}
      </div>

      <Nav
        variant="tabs"
        activeKey={view}
        onSelect={(k) => setView((k ?? "live") as "live" | "reporting")}
        className="mb-3"
      >
        <Nav.Item>
          <Nav.Link eventKey="live">Live</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="reporting">Reporting</Nav.Link>
        </Nav.Item>
      </Nav>

      {view === "reporting" && <SessionMerchantsReportingTab />}

      {view === "live" && (
        <>
          <SessionStatsCards />

          {!captureEnabled && (
            <Alert
              variant="warning"
              className="d-flex align-items-center gap-2 py-2 mb-3"
            >
              <AlertTriangle size={16} />
              <span className="small">
                Headless template capture is currently disabled — &quot;Create
                templates&quot; is greyed out below until it&apos;s turned back
                on.
              </span>
            </Alert>
          )}

          <Card className="mb-3">
            <Card.Body>
              <Row className="align-items-end g-2">
                <Col md={4}>
                  <Form.Label className="small">Domain</Form.Label>
                  <Form.Control
                    size="sm"
                    placeholder="e.g. chelseacharles.com"
                    value={domainInput}
                    onKeyDown={onFilterKeyDown}
                    onChange={(e) => setDomainInput(e.target.value)}
                  />
                </Col>
                <Col md={2}>
                  <Form.Label className="small">Country</Form.Label>
                  <Form.Control
                    size="sm"
                    placeholder="e.g. US"
                    value={countryInput}
                    onKeyDown={onFilterKeyDown}
                    onChange={(e) => setCountryInput(e.target.value)}
                  />
                </Col>
                <Col md={3}>
                  <Form.Label className="small">Run status</Form.Label>
                  <Form.Select
                    size="sm"
                    value={ranStatus}
                    onChange={(e) => {
                      setRanStatus(e.target.value as "" | "never" | "ran");
                      setPage(1);
                    }}
                  >
                    <option value="">All</option>
                    <option value="never">Never ran</option>
                    <option value="ran">Has ran</option>
                  </Form.Select>
                </Col>
                <Col md={3} className="d-flex gap-2">
                  <Button size="sm" variant="primary" onClick={applyFilters}>
                    Search
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={clearFilters}
                  >
                    Clear
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {loading && (
            <div className="text-center py-5">
              <Spinner animation="border" />
            </div>
          )}

          {error && <Alert variant="danger">Failed to load merchants.</Alert>}

          {!loading && !error && (
            <>
              <Table hover responsive size="sm">
                <thead>
                  <tr>
                    <th>Domain</th>
                    <th>Country</th>
                    <th>Run mode</th>
                    <th>Last ran</th>
                    <th>Auto template</th>
                    <th>SKU template</th>
                    <th>AI spend</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {merchants.length === 0 && (
                    <tr>
                      <td colSpan={8} className="text-center text-muted py-4">
                        No merchants with a captured session found.
                      </td>
                    </tr>
                  )}
                  {merchants.map((merchant) => {
                    const captureStatus = captureStatuses[merchant._id] ?? null;
                    const captureInFlight =
                      !!captureStatus &&
                      CAPTURE_RUN_IN_FLIGHT_STATUSES.has(captureStatus.status);
                    // Re-triggerable even once both halves are "created" — a
                    // template can be marked created but later found broken (e.g.
                    // via "Run session test"), and the only way to fix it is to
                    // rebuild. The capture engine already seeds from whatever
                    // Mongo template exists and rediscovers selectors via AI, so
                    // re-running this is exactly "fix it if broken" in practice.
                    const bothCreated = bothTemplatesCreated(merchant);
                    const createDisabled =
                      creatingId === merchant._id ||
                      !captureEnabled ||
                      captureInFlight;
                    const createTitle = !captureEnabled
                      ? "Create templates — headless template capture is currently disabled."
                      : captureInFlight
                        ? "Create templates — a capture run is already in progress for this merchant."
                        : bothCreated
                          ? "Rebuild templates — both halves already exist; this re-runs the AI build to fix them if broken."
                          : "Create templates — queue an unattended AI build of Auto + SKU templates from this merchant's captured session";

                    return (
                      <tr key={merchant._id}>
                        <td>{merchant.homePageUrl || merchant.name}</td>
                        <td>{merchant.countryCode}</td>
                        <td>
                          {merchant.runMode === "session" ? (
                            <Badge bg="dark">session</Badge>
                          ) : (
                            <Badge
                              bg="light"
                              text="dark"
                              title="Has a captured session but runs in another mode — triggering a session run will switch it to session mode"
                            >
                              {merchant.runMode ?? "auto"}
                            </Badge>
                          )}
                        </td>
                        <td className="small text-muted">
                          {lastRan(merchant)}
                        </td>
                        <td>
                          {templateStatusBadge(
                            merchant.autoTemplateCreationStatus
                          )}
                        </td>
                        <td>
                          {templateStatusBadge(
                            merchant.skuTemplateCreationStatus
                          )}
                        </td>
                        <td className="small text-muted">
                          {formatAiSpend(merchant)}
                        </td>
                        <td className="text-end">
                          <div className="d-flex justify-content-end align-items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline-primary"
                              disabled={createDisabled}
                              onClick={() => createTemplates(merchant)}
                              title={createTitle}
                            >
                              {creatingId === merchant._id ? (
                                <Spinner animation="border" size="sm" />
                              ) : (
                                <Zap size={14} />
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-success"
                              disabled={triggeringId === merchant._id}
                              onClick={() => triggerSessionRun(merchant)}
                              title="Run session test — replay the captured session and test templates + coupon codes"
                            >
                              {triggeringId === merchant._id ? (
                                <Spinner animation="border" size="sm" />
                              ) : (
                                <Play size={14} />
                              )}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>

              <div className="d-flex justify-content-between align-items-center mt-2">
                <small className="text-muted">Page {page}</small>
                <div className="d-flex gap-1">
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    disabled={page === 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    Prev
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    disabled={merchants.length < PAGE_SIZE}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};
